/**
 * Color scheme constants for InfoMode plan rendering.
 *
 * Schemes:
 *  - MONO       — single neutral fill (CSS vars only, zero JS DOM mutations)
 *  - ZONES      — day / night / neutral / outdoor zones
 *  - TYPES      — per room-type palette
 *  - COMPARE    — area decile relative to all same-type products (10 buckets: p0…p9)
 *  - BRIGHTNESS — window_area / area_original ratio buckets
 */

export const COLOR_SCHEME = /** @type {const} */ ({
  MONO:       'mono',
  ZONES:      'zones',
  TYPES:      'types',
  COMPARE:    'compare',
  BRIGHTNESS: 'brightness',
})

/**
 * Registry entry shape:
 * {
 *   labelKey: string       — i18n key for UI label
 *   requiresData: boolean  — true if JS per-compartment mutation is needed
 *   dataKey: string|null   — which data source the mutation reads
 * }
 *
 * CSS-only schemes (MONO) need no JS mutations — the base .bim-plan CSS
 * vars (--bim-plan-room-*) already handle the fill via SCSS rules.
 * For other schemes, applyColorSchemeMutations() inlines `path.style.fill`
 * on each compartment path, which overrides the CSS-layer fill at higher
 * specificity (inline style beats class-based rule).
 */
export const COLOR_SCHEME_REGISTRY = {
  [COLOR_SCHEME.MONO]: {
    labelKey:     'product.color_scheme_mono',
    requiresData: false,
    dataKey:      null,
  },
  [COLOR_SCHEME.ZONES]: {
    labelKey:     'product.color_scheme_zones',
    requiresData: true,
    dataKey:      'compartmentTypes',
    // Reads: compartmentType.location ('interior'|'exterior'|'garden')
    //        compartmentType.zone ('day'|'night'|'neutral') when location === 'interior'
    // CSS vars: --bim-color-zone-day, --bim-color-zone-night,
    //           --bim-color-zone-neutral, --bim-color-zone-outdoor
  },
  [COLOR_SCHEME.TYPES]: {
    labelKey:     'product.color_scheme_types',
    requiresData: true,
    dataKey:      'compartmentTypes',
    // Reads: name_cad (e.g. 'idBedroom') → kebab token (e.g. 'bedroom')
    // CSS vars: --bim-color-type-{token}
    //   Defined tokens: bedroom, bathroom, toilet, kitchen, living-room,
    //     living-room-with-annex, corridor, wardrobe, laundry-room, office,
    //     loggia, balcony, terrace, garden
  },
  [COLOR_SCHEME.COMPARE]: {
    labelKey:     'product.color_scheme_compare',
    requiresData: true,
    dataKey:      'allProducts',
    // Reads: area_original of current compartment vs all same-type compartments across all products
    // Buckets: 10 decile buckets (p0 = smallest 10% … p9 = largest 10%)
    // CSS vars: --bim-color-compare-p0 … --bim-color-compare-p9
  },
  [COLOR_SCHEME.BRIGHTNESS]: {
    labelKey:     'product.color_scheme_brightness',
    requiresData: false,
    dataKey:      null,
    // Reads: productCompartments[].window_area / area_original ratio
    // ratio = (window_area / area_original) * 8  (1.0 = legal minimum = 1/8 floor area)
    // no windows → null fill (CSS fallback to room_default)
    // ratio < 1.0 → blue; ratio 1.0–3.0 → white→yellow; ratio > 3.0 → full yellow
    // CSS vars: --bim-color-brightness-none, --bim-color-brightness-low,
    //           --bim-color-brightness-high
  },
}

/** Ordered list for UI display. */
export const COLOR_SCHEME_ORDER = [
  COLOR_SCHEME.MONO,
  COLOR_SCHEME.ZONES,
  COLOR_SCHEME.TYPES,
  COLOR_SCHEME.COMPARE,
  COLOR_SCHEME.BRIGHTNESS,
]

/**
 * Ordered zone keys for ZONES scheme legend and hover logic.
 * Order matches the CSS vars: --bim-color-zone-{key}.
 */
export const ZONE_KEYS = /** @type {const} */ (['day', 'night', 'neutral', 'outdoor'])

/**
 * Compare bucket keys for COMPARE scheme legend and hover logic.
 * p0 = smallest decile … p9 = largest decile.
 */
export const COMPARE_KEYS = /** @type {const} */ (
  Array.from({ length: 10 }, (_, i) => `p${i}`)
)
